export type PriceRankCalendarDefaultType = 'none' | 'weekday' | 'holiday' | 'pre_holiday'

export type PriceRankColorKey = 'black' | 'gray' | 'brown' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple' | 'pink' | 'red'

export type PriceRank = {
  id: string
  displayOrder: number
  shortName: string
  name: string
  color: string
  calendarDefaultType: PriceRankCalendarDefaultType
}

export type PriceRankFormValues = {
  displayOrder: string
  shortName: string
  name: string
  color: string
  calendarDefaultType: PriceRankCalendarDefaultType
}

export type PriceRankInput = {
  displayOrder: number
  shortName: string
  name: string
  color: string
  calendarDefaultType: PriceRankCalendarDefaultType
}

export const PRICE_RANK_COLORS: Record<PriceRankColorKey, { color: string; labelKey: string }> = {
  black: { color: '#5E6772', labelKey: 'priceRank.color.black' },
  gray: { color: '#8F98A3', labelKey: 'priceRank.color.gray' },
  brown: { color: '#A67C5B', labelKey: 'priceRank.color.brown' },
  orange: { color: '#D9924F', labelKey: 'priceRank.color.orange' },
  yellow: { color: '#DDB954', labelKey: 'priceRank.color.yellow' },
  green: { color: '#69B86A', labelKey: 'priceRank.color.green' },
  blue: { color: '#5C93D6', labelKey: 'priceRank.color.blue' },
  purple: { color: '#A57BC9', labelKey: 'priceRank.color.purple' },
  pink: { color: '#D97AB7', labelKey: 'priceRank.color.pink' },
  red: { color: '#D95F4A', labelKey: 'priceRank.color.red' },
}

export const PRICE_RANK_COLOR_ORDER: PriceRankColorKey[] = [
  'black',
  'gray',
  'brown',
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
]

export const PRICE_RANK_CALENDAR_DEFAULT_TYPES: PriceRankCalendarDefaultType[] = ['none', 'weekday', 'holiday', 'pre_holiday']

export const isPriceRankCalendarDefaultType = (type: string): type is PriceRankCalendarDefaultType =>
  PRICE_RANK_CALENDAR_DEFAULT_TYPES.includes(type as PriceRankCalendarDefaultType)

export const getCalendarDefaultLabelKey = (type: string): string | undefined => {
  if (type === 'none' || !isPriceRankCalendarDefaultType(type)) {
    return undefined
  }
  if (type === 'pre_holiday') {
    return 'priceRank.calendarDefault.dayBeforeHoliday'
  }
  return `priceRank.calendarDefault.${type}`
}

export const isPriceRankColorKey = (color: string): color is PriceRankColorKey =>
  Object.prototype.hasOwnProperty.call(PRICE_RANK_COLORS, color)

export const getPriceRankColorHex = (color: string): string => (isPriceRankColorKey(color) ? PRICE_RANK_COLORS[color].color : color)

/** API 保存用。UI の key は hex に変換（BE 例・アサイン表示は hex 前提） */
export const toApiPriceRankColor = (color: string): string => getPriceRankColorHex(color)

/** GET の color（hex または旧 key）を UI 選択用 key に戻す。未知は undefined */
export const resolvePriceRankColorKeyFromApi = (color: string): PriceRankColorKey | undefined => {
  if (isPriceRankColorKey(color)) {
    return color
  }
  const normalized = color.trim().toLowerCase()
  const found = (Object.keys(PRICE_RANK_COLORS) as PriceRankColorKey[]).find(
    key => PRICE_RANK_COLORS[key].color.toLowerCase() === normalized,
  )
  return found
}

/** ランク未解決・マスタ不整合時のヘッダー色（gray パレットと同値だが用途が異なる） */
export const UNRESOLVED_RANK_COLOR = '#8F98A3'

export const BASE_RATE_CELL_TINT: Record<PriceRankColorKey, string> = {
  blue: '#EEF5FD',
  green: '#EDF8EE',
  yellow: '#FDF8EA',
  orange: '#FDF4EC',
  brown: '#F8F2EC',
  pink: '#FDF0F7',
  purple: '#F5F0FA',
  red: '#FDF0EE',
  black: '#F2F2F2',
  gray: '#F5F5F5',
}

export const BASE_RATE_CELL_DEFAULT_TINT = '#fff'

const isValidHexColor = (color: string) => /^#([0-9a-fA-F]{3}){1,2}$/.test(color.trim())

export const resolvePriceRankColorKey = (color: string): PriceRankColorKey | undefined => {
  if (isPriceRankColorKey(color)) {
    return color
  }
  return resolvePriceRankColorKeyFromApi(color)
}

export const resolvePriceRankHeaderColor = (color: string | null | undefined): string => {
  if (!color) {
    return UNRESOLVED_RANK_COLOR
  }
  const key = resolvePriceRankColorKey(color)
  if (key) {
    return getPriceRankColorHex(key)
  }
  const normalized = color.trim()
  return isValidHexColor(normalized) ? normalized : UNRESOLVED_RANK_COLOR
}

export const resolvePriceRankCellTint = (color: string | null | undefined): string => {
  if (!color) {
    return BASE_RATE_CELL_DEFAULT_TINT
  }
  const key = resolvePriceRankColorKey(color)
  return key ? BASE_RATE_CELL_TINT[key] : BASE_RATE_CELL_DEFAULT_TINT
}
